import { Button, InfoBox } from "@tredespace/ui/widgets";
import { Fragment, type JSX } from "react";
import type { BomRow } from "../../engine/bom.ts";
import { GROUP_ORDER } from "../../engine/bom.ts";
import { exportComponentList } from "../../state/bom/bom.actions.ts";
import { bomState } from "../../state/bom/bom.state.ts";

const HEAD_CLASS = "py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400";

function groupedRows(rows: readonly BomRow[]): { group: string; rows: BomRow[] }[] {
  return GROUP_ORDER.map((group) => ({ group, rows: rows.filter((row) => row.group === group) })).filter(
    (entry) => entry.rows.length > 0,
  );
}

/**
 * The component list.
 *
 * Rows are collected during rendering, so this always equals what is drawn —
 * art. nos. and weights per RISE C900677 and the HAKI drawings.
 */
export function BomPanel(): JSX.Element {
  const { rows, totalKg, anyNonModuleDiagonal } = bomState.use();
  const groups = groupedRows(rows);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {groups.length === 0 && <p className="text-xs text-slate-400">Nothing built yet.</p>}

        {groups.length > 0 && (
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b-2 border-slate-400/50">
                <th className={`${HEAD_CLASS} text-left`}>Component</th>
                <th className={`${HEAD_CLASS} text-left`}>Art. no.</th>
                <th className={`${HEAD_CLASS} text-right`}>Qty</th>
                <th className={`${HEAD_CLASS} text-right`}>kg</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((entry) => (
                <Fragment key={entry.group}>
                  <tr>
                    <td
                      colSpan={4}
                      className="pt-3 pb-0.5 text-[10px] font-bold uppercase tracking-wider text-sky-400"
                    >
                      {entry.group}
                    </td>
                  </tr>
                  {entry.rows.map((row) => (
                    <tr key={`${row.group}|${row.name}`} className="border-b border-slate-700/40">
                      <td className="py-0.5 pr-2 align-top">{row.name}</td>
                      <td className="py-0.5 pr-2 align-top whitespace-nowrap font-mono text-[11px] text-slate-400">
                        {row.art}
                      </td>
                      <td className="py-0.5 pr-2 text-right align-top font-mono">{row.n}</td>
                      <td className="py-0.5 text-right align-top font-mono">{(row.kg * row.n).toFixed(1)}</td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}

        {anyNonModuleDiagonal && (
          <div className="mt-3">
            <InfoBox>
              Diagonal brace DS AL exists for modules 1250–3050. Other lengths are counted as Ø48 tube +
              couplers.
            </InfoBox>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-t-2 border-slate-400/50 px-3 py-2 text-sm font-bold">
        <span>Total weight</span>
        <span className="font-mono">{Math.round(totalKg).toLocaleString("en")} kg</span>
      </div>
      <div className="px-3 pb-3">
        <Button
          onClick={() => void exportComponentList()}
          disabled={rows.length === 0}
          tooltip={"Export the list as .xlsx\nOne flat sheet with a header row"}
        >
          Export list (Excel)
        </Button>
      </div>
    </div>
  );
}
